import React, { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Merge, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import ticketsService from '@/services/tickets.service';

interface MergeSuggestionBannerProps {
  ticketId: string;
  /** Wird aufgerufen, wenn das aktuelle Ticket in ein anderes zusammengeführt wurde. */
  onOpenTicket: (ticketId: string) => void;
}

/**
 * Hinweis über der Konversation: Es gibt bereits ein offenes Ticket zur selben
 * Bestellung (bzw. vom selben Kunden in den letzten 30 Tagen). Ein Klick führt
 * zusammen; das ältere Ticket bleibt Haupt-Ticket. Ausblenden gilt pro Ticket
 * für diese Sitzung.
 */
export default function MergeSuggestionBanner({ ticketId, onOpenTicket }: MergeSuggestionBannerProps) {
  const queryClient = useQueryClient();
  const [dismissed, setDismissed] = useState<Record<string, true>>(() => {
    try { return JSON.parse(sessionStorage.getItem('merge-suggestions-dismissed') || '{}'); } catch { return {}; }
  });

  const { data } = useQuery({
    queryKey: ['merge-suggestions', ticketId],
    queryFn: () => ticketsService.getMergeSuggestions(ticketId),
    enabled: Boolean(ticketId),
    staleTime: 60_000,
  });

  const mergeMutation = useMutation({
    mutationFn: ({ parentId, childId }: { parentId: string; childId: string }) =>
      ticketsService.mergeTickets(parentId, [childId]),
    onSuccess: (res, vars) => {
      queryClient.invalidateQueries({ queryKey: ['ticket'] });
      queryClient.invalidateQueries({ queryKey: ['mergedTickets'] });
      queryClient.invalidateQueries({ queryKey: ['merge-suggestions'] });
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      toast.success(`Zusammengeführt – Haupt-Ticket: ${res.data.parentTicket.ticket_number}`);
      if (vars.parentId !== ticketId) onOpenTicket(vars.parentId);
    },
    onError: (error: any) => {
      toast.error(error?.response?.data?.message || 'Tickets konnten nicht zusammengeführt werden', { duration: 10000 });
    },
  });

  const suggestions = (data?.data?.suggestions || []).filter((s) => !dismissed[`${ticketId}:${s.id}`]);
  if (suggestions.length === 0) return null;

  const dismiss = (id: string) => {
    const next = { ...dismissed, [`${ticketId}:${id}`]: true as const };
    setDismissed(next);
    try { sessionStorage.setItem('merge-suggestions-dismissed', JSON.stringify(next)); } catch { /* ignore */ }
  };

  return (
    <div className="mx-4 mt-3 space-y-2">
      {suggestions.map((s) => {
        const reason = s.reason === 'order'
          ? `zur selben Bestellung ${s.shared_orders.join(', ')}`
          : 'vom selben Kunden (letzte 30 Tage)';
        const intoOther = s.suggested_parent === s.id;
        const pending = mergeMutation.isPending && mergeMutation.variables?.childId === (intoOther ? ticketId : s.id);
        return (
          <div key={s.id} className="flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-900">
            <Merge className="h-4 w-4 flex-shrink-0 text-amber-700" />
            <div className="min-w-0 flex-1">
              <span>Offenes Ticket {reason}: </span>
              <button
                type="button"
                className="font-semibold underline decoration-amber-400 underline-offset-2 hover:text-amber-700"
                onClick={() => onOpenTicket(s.id)}
                title={s.subject}
              >
                {s.ticket_number}
              </button>
              <span className="text-amber-800/80"> – {s.subject}</span>
            </div>
            <button
              type="button"
              disabled={mergeMutation.isPending}
              onClick={() => mergeMutation.mutate(intoOther ? { parentId: s.id, childId: ticketId } : { parentId: ticketId, childId: s.id })}
              className="flex flex-shrink-0 items-center gap-1 rounded-md bg-amber-600 px-2.5 py-1 text-xs font-medium text-white hover:bg-amber-700 disabled:opacity-60"
            >
              {pending && <Loader2 className="h-3 w-3 animate-spin" />}
              {intoOther ? `In ${s.ticket_number} zusammenführen` : `${s.ticket_number} hierher zusammenführen`}
            </button>
            <button
              type="button"
              onClick={() => dismiss(s.id)}
              className="flex-shrink-0 rounded p-1 text-amber-700 hover:bg-amber-100"
              aria-label="Vorschlag ausblenden"
              title="Ausblenden"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
